import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { useQuery } from "@tanstack/react-query";
import { getVisibleUserCategories } from "@/services/categories.service";
import CategoryPickerDialog from "@/components/common/CategoryPickerDialog";
import UnitPickerDialog from "./UnitPickerDialog";
import { Pencil, Loader2 } from "lucide-react";

interface NewProductFormProps {
  name: string;
  setName: (val: string) => void;
  category: string;
  setCategory: (category: string) => void;
  unit: string;
  setUnit: (unit: string) => void;
  quantity?: string;
  setQuantity?: (quantity: string) => void;
  onSubmit?: () => Promise<void> | void;
  onCreateProduct?: (closeAfter: boolean) => Promise<void> | void;
  isLoading?: boolean;
  onCancel?: () => void;
  style?: React.CSSProperties;
}



const NewProductForm = ({
  name,
  setName,
  category,
  setCategory,
  unit,
  setUnit,
  quantity,
  setQuantity,
  onSubmit,
  onCreateProduct,
  isLoading,
  onCancel,
  style
}: NewProductFormProps) => {
  const { data: categories = [] } = useQuery({
    queryKey: ["visible-user-categories"],
    queryFn: getVisibleUserCategories,
    staleTime: 30000,
  });

  const { theme } = useThemeConfig();

  const categoriesWithNone = [
    {
      id: "none",
      name: "Nessuna categoria",
      icon: "",
      color: theme.cardBackgroundColor,
      textColor: theme.textColor,
    },
    ...categories,
  ];
  const selectedCategory = categoriesWithNone.find(cat => cat.id === category) || categoriesWithNone[0];
  const categoryLabel = `${selectedCategory.icon ? selectedCategory.icon + " " : ""}${selectedCategory.name}`;
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [isUnitDialogOpen, setIsUnitDialogOpen] = useState(false);

  return (
    <div className="border p-4 rounded-md h-full flex flex-col" style={style}>
      <div className="flex-1 overflow-y-auto">
        {/* Categoria e Unità affiancate con proporzione 2/3 + 1/3 */}
        <div className="grid grid-cols-3 gap-4 mb-2 items-start">
          {/* Categoria - 2/3 */}
          <div className="col-span-2">
            <Label htmlFor="product-category-display">Categoria</Label>
            <div className="flex items-center mt-1">
              <div
                id="product-category-display"
                className="flex-1 border rounded-md px-2 py-2"
              >
                {categoryLabel}
              </div>
              <Button
                aria-label="Modifica categoria"
                variant="outline"
                size="icon"
                className="ml-2"
                onClick={() => setIsCategoryDialogOpen(true)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
            <CategoryPickerDialog
              open={isCategoryDialogOpen}
              onOpenChange={setIsCategoryDialogOpen}
              categories={categoriesWithNone}
              selectedId={category}
              onSelect={(id) => setCategory(id)}
            />
          </div>

          {/* Unità - 1/3 */}
          <div className="col-span-1">
            <Label htmlFor="product-unit-display">Un. Misura</Label>
            <div className="flex items-center mt-1">
              <div
                id="product-unit-display"
                className="flex-1 border rounded-md px-2 py-2"
              >
                {unit}
              </div>
              <Button
                aria-label="Modifica unità"
                variant="outline"
                size="icon"
                className="ml-2"
                onClick={() => setIsUnitDialogOpen(true)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
            <UnitPickerDialog
              open={isUnitDialogOpen}
              onOpenChange={setIsUnitDialogOpen}
              selectedUnit={unit}
              onSelect={(val) => setUnit(val)}
            />
          </div>
        </div>

        {/* Quantità */}
        {setQuantity && (
          <div className="mb-2">
            <Label htmlFor="product-quantity">Quantità</Label>
            <Input
              data-keyboard-scroll
              id="product-quantity"
              value={quantity || "1"}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Quantità"
              className="mt-1"
              type="number"
            />
          </div>
        )}
      </div>

      {/* Pulsanti */}
      <div className="flex flex-wrap gap-2 mt-2 pt-2 pb-[calc(var(--safe-area-bottom)+0.25rem)]">
        <Button
          onClick={() => onCreateProduct ? onCreateProduct(false) : onSubmit?.()}
          className="hover:bg-[var(--btn-bg-hover)] flex-1 whitespace-normal h-auto"
          disabled={isLoading}
          style={{
            backgroundColor: theme.primaryColor,
            color: theme.textPrimaryColor,
            fontWeight: 'bold',
            '--btn-bg-hover': theme.primaryColor,
          } as React.CSSProperties}
        >
          {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />}
          Crea e Torna in Ricerca
        </Button>
        <Button
          onClick={() => onCreateProduct ? onCreateProduct(true) : onSubmit?.()}
          className="hover:bg-[var(--btn-bg-hover)] flex-1 whitespace-normal h-auto"
          disabled={isLoading}
          style={{
            backgroundColor: theme.primaryColor,
            color: theme.textPrimaryColor,
            fontWeight: 'bold',
            '--btn-bg-hover': theme.primaryColor,
          } as React.CSSProperties}
        >
          {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />}
          Crea e Torna in Lista
        </Button>
        {onCancel && (
          <Button
            onClick={onCancel}
            variant="outline"
            disabled={isLoading}
            className="w-full"
            style={{
              backgroundColor: theme.backgroundColor,
              color: theme.textColor,
              borderRadius: theme.buttonBorderRadius,
              padding: theme.buttonPadding,
              fontWeight: theme.buttonFontWeight,
              transitionDuration: theme.transitionSpeed,
              '--btn-bg-hover': theme.primaryColor,
            } as React.CSSProperties}
          >
            Annulla
          </Button>
        )}
      </div>
    </div>
  );
};

export default NewProductForm;
